import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import Layout from '../../components/Layout';
import { sideBarItems } from '../../utils';
import SubscriptionService from '../../services/admin/SubscriptionService';

const initialFilters = {
  search: '',
  status: 'all',
  mode: 'all',
  fromDate: '',
  toDate: ''
};

function SubscriptionPayments() {
  const [filters, setFilters] = useState(initialFilters);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAll().catch(() => {});
  }, [page]);

  const getError = (err) => err?.response?.data?.error || 'Operation failed';

  const buildParams = () => {
    const params = { page, limit };
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.mode !== 'all') params.mode = filters.mode;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    return params;
  };

  const loadAll = async (forceFirstPage = false) => {
    setLoading(true);
    try {
      const nextPage = forceFirstPage ? 1 : page;
      const params = { ...buildParams(), page: nextPage };
      const [paymentsRes, financialRes] = await Promise.all([
        SubscriptionService.getSubscriptionPayments(params),
        SubscriptionService.getFinancialSummary(params)
      ]);

      const paymentPayload = paymentsRes?.data || {};
      setPayments(paymentPayload?.data || []);
      setSummary(paymentPayload?.summary || null);
      setTotalPages(paymentPayload?.pagination?.totalPages || 1);
      setFinancialSummary(financialRes?.data || null);

      if (forceFirstPage) setPage(1);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const onApplyFilters = async () => {
    await loadAll(true);
  };

  const onResetFilters = async () => {
    setFilters(initialFilters);
    setPage(1);
    setLoading(true);
    try {
      const [paymentsRes, financialRes] = await Promise.all([
        SubscriptionService.getSubscriptionPayments({ page: 1, limit }),
        SubscriptionService.getFinancialSummary({ page: 1, limit })
      ]);
      const paymentPayload = paymentsRes?.data || {};
      setPayments(paymentPayload?.data || []);
      setSummary(paymentPayload?.summary || null);
      setTotalPages(paymentPayload?.pagination?.totalPages || 1);
      setFinancialSummary(financialRes?.data || null);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const renderMode = (type) => {
    if (type === 'subscription_purchase') return 'Purchase';
    if (type === 'subscription_renewal') return 'Renewal';
    if (type === 'subscription_manual') return 'Manual/Offline';
    return 'N/A';
  };

  const exportCsv = () => {
    if (!payments.length) {
      toast.error('No payment data to export');
      return;
    }

    const headers = ['Transaction ID', 'Driver Name', 'Driver Mobile', 'Pack', 'Duration', 'Mode', 'Amount', 'Status', 'bKash Payment ID', 'Date'];
    const rows = payments.map((item) => [
      item._id,
      item?.driver?.name || 'N/A',
      item?.driver?.mobile || 'N/A',
      item?.subscriptionPack?.name || 'N/A',
      item?.subscriptionPack?.durationDays || 0,
      renderMode(item.transactionType),
      `${item.currency || 'BDT'} ${Number(item.amount || 0).toFixed(2)}`,
      item.status,
      item.bkashPaymentId || 'N/A',
      item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `subscription-payments-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPdfView = () => {
    if (!payments.length) {
      toast.error('No payment data to export');
      return;
    }

    const rows = payments.map((item) => `
      <tr>
        <td>${item._id}</td>
        <td>${item?.driver?.name || 'N/A'} (${item?.driver?.mobile || 'N/A'})</td>
        <td>${item?.subscriptionPack?.name || 'N/A'}</td>
        <td>${renderMode(item.transactionType)}</td>
        <td>${item.currency || 'BDT'} ${Number(item.amount || 0).toFixed(2)}</td>
        <td>${item.status}</td>
        <td>${item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'}</td>
      </tr>
    `).join('');

    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Subscription Payments Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
        th { background: #f5f5f5; }
      </style></head>
      <body>
        <h2>Subscription Payments Report</h2>
        <table>
          <thead><tr><th>Transaction ID</th><th>Driver</th><th>Pack</th><th>Mode</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Layout title={sideBarItems.subscriptionPayments.key}>
      <div className="w-full py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Subscription Payments</h1>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <input className="w-full px-3 py-2 border rounded-md" placeholder="Search Txn ID / bKash ID / Idempotency" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
            <select className="w-full px-3 py-2 border rounded-md" value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="initiated">Initiated</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="requested">Requested</option>
            </select>
            <select className="w-full px-3 py-2 border rounded-md" value={filters.mode} onChange={(e) => setFilters((prev) => ({ ...prev, mode: e.target.value }))}>
              <option value="all">All Mode</option>
              <option value="purchase">Purchase</option>
              <option value="renewal">Renewal</option>
              <option value="manual">Manual/Offline</option>
            </select>
            <input className="w-full px-3 py-2 border rounded-md" type="date" value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} />
            <input className="w-full px-3 py-2 border rounded-md" type="date" value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} />
          </div>

          <div className="mt-4 flex gap-2">
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50" onClick={onApplyFilters} disabled={loading}>Apply</button>
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50" onClick={onResetFilters} disabled={loading}>Reset</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white shadow-md rounded-lg p-4"><p className="text-sm text-gray-500">Total Income (Success)</p><p className="text-2xl font-bold">BDT {Number(financialSummary?.profitLoss?.totalIncome || summary?.successAmount || 0).toFixed(2)}</p></div>
          <div className="bg-white shadow-md rounded-lg p-4"><p className="text-sm text-gray-500">Total Expense</p><p className="text-2xl font-bold">BDT {Number(financialSummary?.profitLoss?.totalExpense || 0).toFixed(2)}</p></div>
          <div className="bg-white shadow-md rounded-lg p-4"><p className="text-sm text-gray-500">Profit / Loss</p><p className="text-2xl font-bold">BDT {Number(financialSummary?.profitLoss?.profit || 0).toFixed(2)}</p></div>
          <div className="bg-white shadow-md rounded-lg p-4"><p className="text-sm text-gray-500">Manual/Offline Payments</p><p className="text-2xl font-bold">{summary?.manualCount || 0}</p></div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <h2 className="text-lg font-semibold">Subscription Payment Logs</h2>
            <div className="flex gap-2">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200" onClick={exportCsv}>Export CSV</button>
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200" onClick={exportPdfView}>Export PDF</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-3 py-2 border">Transaction ID</th><th className="px-3 py-2 border">Driver</th><th className="px-3 py-2 border">Pack</th><th className="px-3 py-2 border">Mode</th><th className="px-3 py-2 border">Amount</th><th className="px-3 py-2 border">Status</th><th className="px-3 py-2 border">bKash Payment ID</th><th className="px-3 py-2 border">Date</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((item) => (
                  <tr key={item._id} className="text-sm">
                    <td className="px-3 py-2 border">{item._id}</td>
                    <td className="px-3 py-2 border"><div>{item?.driver?.name || 'N/A'}</div><div className="text-xs text-gray-500">{item?.driver?.mobile || 'N/A'}</div></td>
                    <td className="px-3 py-2 border"><div>{item?.subscriptionPack?.name || 'N/A'}</div><div className="text-xs text-gray-500">{item?.subscriptionPack?.durationDays || 0} days</div></td>
                    <td className="px-3 py-2 border">{renderMode(item.transactionType)}</td>
                    <td className="px-3 py-2 border">{item.currency || 'BDT'} {Number(item.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 border">{item.status}</td>
                    <td className="px-3 py-2 border">{item.bkashPaymentId || 'N/A'}</td>
                    <td className="px-3 py-2 border">{item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'}</td>
                  </tr>
                ))}
                {!payments.length && (
                  <tr><td colSpan="8" className="px-3 py-10 border text-center text-gray-500">{loading ? 'Loading...' : 'No subscription payments found.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center items-center mt-6 space-x-4">
            <button onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page === 1 || loading} className={`px-5 py-2 rounded-lg text-white font-semibold transition-all duration-300 ${page === 1 || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 active:scale-95'}`}>⬅ Previous</button>
            <span className="text-lg font-semibold text-gray-700">Page <span className="text-green-600">{page}</span> of <span className="text-green-600">{totalPages}</span></span>
            <button onClick={() => setPage((prev) => (prev < totalPages ? prev + 1 : prev))} disabled={page >= totalPages || loading} className={`px-5 py-2 rounded-lg text-white font-semibold transition-all duration-300 ${page >= totalPages || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 active:scale-95'}`}>Next ➡</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SubscriptionPayments;
